import React from 'react';

import type { ChartV2Interval } from '../types/chart-v2.types';
import { Chip } from './Chip';
import { Dropdown } from './Dropdown';
import { ChevronDownIcon } from './Icons';
import styles from './IntervalSelector.module.scss';

/**
 * Props for `<IntervalSelector>`.
 *
 * Required: `intervals`, `selected`, `onChange`. Renders a chip-shaped
 * trigger that opens a menu of resample intervals; intraday and daily+
 * groups are visually separated.
 */
export interface IntervalSelectorProps {
  /** Intervals to render in the dropdown, in display order. */
  intervals: ChartV2Interval[];
  /** Currently active interval. */
  selected: ChartV2Interval;
  /**
   * Subset of `intervals` available for selection — anything outside the
   * list is rendered greyed-out. Useful when the backend returns empty
   * data for certain resamples on a given asset.
   */
  enabledIntervals?: ChartV2Interval[];
  /** Disables the trigger entirely. */
  disabled?: boolean;
  /** Called with the picked interval when the user selects a row. */
  onChange: (interval: ChartV2Interval) => void;
  /**
   * Per-interval label overrides shown inside the menu. Defaults to the
   * full English name (`"1 minute"`, `"1 day"`, …).
   */
  locales?: Partial<Record<ChartV2Interval, string>>;
  /**
   * Per-interval label overrides for the trigger chip only. Defaults to
   * `locales` (and then the English name) so passing this lets you show
   * a compact label on the trigger and a verbose one in the menu.
   */
  triggerLocales?: Partial<Record<ChartV2Interval, string>>;
}

export const DEFAULT_INTERVAL_LOCALES: Record<ChartV2Interval, string> = {
  '1m': '1 minute',
  '5m': '5 minutes',
  '15m': '15 minutes',
  '30m': '30 minutes',
  '1h': '1 hour',
  '1D': '1 day',
  '1W': '1 week',
  '1M': '1 month',
};

const INTRADAY: ChartV2Interval[] = ['1m', '5m', '15m', '30m', '1h'];

const isIntraday = (i: ChartV2Interval) => INTRADAY.includes(i);

/**
 * Chip-shaped trigger for picking a chart's resample interval (`"1m"`,
 * `"1D"`, `"1W"`, etc.). Opens a `Dropdown` menu showing every
 * interval in `intervals`, with a divider between the intraday group
 * (`1m`–`1h`) and the daily-and-up group.
 *
 * Use `enabledIntervals` to render the unsupported intervals as
 * disabled rows; use `triggerLocales` to show a compact label on the
 * trigger and a more verbose one inside the menu.
 */
export const IntervalSelector = ({
  intervals,
  selected,
  enabledIntervals,
  disabled,
  onChange,
  locales,
  triggerLocales,
}: IntervalSelectorProps) => {
  const labelOf = (i: ChartV2Interval) =>
    locales?.[i] ?? DEFAULT_INTERVAL_LOCALES[i];

  const items = intervals.map((i, idx) => {
    const prev = intervals[idx - 1];
    const separated = prev !== undefined && isIntraday(prev) && !isIntraday(i);

    return {
      value: i,
      label: labelOf(i),
      disabled: enabledIntervals ? !enabledIntervals.includes(i) : false,
      separated,
    };
  });

  const triggerLabel = triggerLocales?.[selected] ?? labelOf(selected);

  return (
    <div className={styles.root} data-testid="chartv2-interval-selector">
      <Dropdown
        items={items}
        selected={selected}
        onChange={onChange}
        disabled={disabled}
        data-testid="chartv2-interval-dropdown"
        menuMinWidth={185}
        renderTrigger={({ toggle }) => (
          <Chip
            className={styles.trigger}
            label={triggerLabel}
            rightIcon={<ChevronDownIcon />}
            disabled={disabled}
            onClick={toggle}
            data-testid="chartv2-interval-trigger"
          />
        )}
      />
    </div>
  );
};
